var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { COMMUNICATION_CHART_CROSS_TEAM_AGGREGATE_NODE_ID, COMMUNICATION_CHART_EXTERNAL_NODE_ID, COMMUNICATION_CHART_EXTERNAL_NODE_NAME, COMMUNICATION_CHART_OTHER_NODE_ID } from '@web/components/CommunicationNetworkGraph/core/communicationChart/constants';
import { CommunicationChartNodeKindEnum, isCommunicationChartCrossTeamNodeKind, isCommunicationChartHubNodeKind, isCommunicationChartPersonNode } from '@web/components/CommunicationNetworkGraph/core/communicationChart/nodeKind';
import { getCommunicationChartNodeSize, truncateCommunicationChartLabel } from '@web/components/CommunicationNetworkGraph/core/communicationChart/nodeMetrics';
import { CommunicationChartScopeEnum, isCommunicationChartEmployeeScope, isCommunicationChartOrgScope, isCommunicationChartTeamScope } from '@web/components/CommunicationNetworkGraph/core/communicationChart/types';
import { getCommunicationChartPersonInitials, isCommunicationChartOrgTeamNode, joinCommunicationChartElementClasses } from '@web/components/CommunicationNetworkGraph/core/communicationChart/utils';
import { communicationChartCenterPosition, communicationChartCrossTeamColumnPosition, communicationChartEmployeeCrossTeamColumnPosition, communicationChartEmployeeTeammateColumnPosition, communicationChartExternalNodePosition, communicationChartRingNodePosition, partitionCommunicationChartEmployeeNodes, partitionCommunicationChartNodes, resolveHubSatelliteTeamId, resolveSyntheticNodePosition } from '@web/components/CommunicationNetworkGraph/graph/chartLayout/communicationChartPositions';
import { COMMUNICATION_CHART_EMPLOYEE_FOCUS_NODE_SIZE_MULTIPLIER, COMMUNICATION_CHART_HUB_NODE_SIZE_MULTIPLIER, COMMUNICATION_CHART_ORG_SCOPE_NODE_SIZE_MULTIPLIER, COMMUNICATION_CHART_TEAM_SCOPE_PERSON_ORG_TEAM_MEMBER_COUNT } from '@web/components/CommunicationNetworkGraph/graph/communicationChartConstants';
import { usesCommunicationChartOrgTeamHubReveal } from '@web/components/CommunicationNetworkGraph/graph/communicationChartOrgTeamHubVisibility';
var scaleCommunicationChartHubNodeSize = function (size) {
    return Math.round(size * COMMUNICATION_CHART_HUB_NODE_SIZE_MULTIPLIER);
};
var clampCommunicationChartOrgTeamSizeMembers = function (members) {
    return Math.min(14, Math.max(3, members));
};
var resolveCommunicationChartOrgTeamSizeBucketIndex = function (members) {
    var clampedMembers = clampCommunicationChartOrgTeamSizeMembers(members);
    if (clampedMembers <= 4) {
        return 0;
    }
    if (clampedMembers <= 6) {
        return 1;
    }
    if (clampedMembers <= 8) {
        return 2;
    }
    if (clampedMembers <= 10) {
        return 3;
    }
    if (clampedMembers <= 12) {
        return 4;
    }
    return 5;
};
var COMMUNICATION_CHART_ORG_TEAM_SIZE_BUCKET_MULTIPLIERS = [
    25 / 45,
    35 / 45,
    1,
    60 / 45,
    75 / 45,
    2
];
var resolveCommunicationChartOrgTeamBucketedNodeSize = function (baseOrgTeamSizePx, memberCount) {
    var bucketIndex = resolveCommunicationChartOrgTeamSizeBucketIndex(memberCount);
    return Math.round(baseOrgTeamSizePx *
        COMMUNICATION_CHART_ORG_TEAM_SIZE_BUCKET_MULTIPLIERS[bucketIndex]);
};
var scaleCommunicationChartOrgScopeNodeSize = function (size, scope) {
    return isCommunicationChartOrgScope(scope)
        ? Math.round(size * COMMUNICATION_CHART_ORG_SCOPE_NODE_SIZE_MULTIPLIER)
        : size;
};
var resolveCommunicationChartTeamScopePersonNodeSize = function (nodeSizeMax) {
    return resolveCommunicationChartOrgTeamBucketedNodeSize(scaleCommunicationChartOrgScopeNodeSize(nodeSizeMax, CommunicationChartScopeEnum.Org), COMMUNICATION_CHART_TEAM_SCOPE_PERSON_ORG_TEAM_MEMBER_COUNT);
};
var resolveCommunicationChartTeamScopeManagerNodeSize = function (nodeSizeMax) {
    return Math.round(resolveCommunicationChartTeamScopePersonNodeSize(nodeSizeMax) *
        COMMUNICATION_CHART_EMPLOYEE_FOCUS_NODE_SIZE_MULTIPLIER);
};
var resolveCommunicationChartNodeSize = function (node, degree, maxDegree, nodeSizeMin, nodeSizeMax) {
    return isCommunicationChartPersonNode(node)
        ? nodeSizeMax
        : getCommunicationChartNodeSize(degree, maxDegree, nodeSizeMin, nodeSizeMax);
};
var nodePersonData = function (node) { return (__assign({ initials: getCommunicationChartPersonInitials(node.name) }, (node.avatarUrl ? { avatarUrl: node.avatarUrl } : {}))); };
var personNodeClasses = function (node, options) {
    return joinCommunicationChartElementClasses('personNode', 'hasAvatar', isCommunicationChartCrossTeamNodeKind(node.kind) &&
        CommunicationChartNodeKindEnum.CrossTeam, (options === null || options === void 0 ? void 0 : options.teamScopeDashboardPerson) && 'teamScopePerson');
};
var resolveSyntheticNodeKindClass = function (node) {
    if (node.nodeId === COMMUNICATION_CHART_EXTERNAL_NODE_ID) {
        return CommunicationChartNodeKindEnum.External;
    }
    if (node.nodeId === COMMUNICATION_CHART_CROSS_TEAM_AGGREGATE_NODE_ID) {
        return CommunicationChartNodeKindEnum.CrossTeamAggregate;
    }
    if (node.nodeId === COMMUNICATION_CHART_OTHER_NODE_ID) {
        return CommunicationChartNodeKindEnum.Other;
    }
    if (isCommunicationChartHubNodeKind(node.kind)) {
        return node.kind;
    }
    return undefined;
};
var resolveSyntheticNodeSize = function (node, context) {
    var isExternal = node.nodeId === COMMUNICATION_CHART_EXTERNAL_NODE_ID;
    var isCrossTeamAggregate = node.nodeId === COMMUNICATION_CHART_CROSS_TEAM_AGGREGATE_NODE_ID;
    if (isExternal || isCrossTeamAggregate) {
        if (context.isDashboardTeamScope) {
            return context.teamScopeSyntheticNodeSize;
        }
        if (isExternal) {
            return scaleCommunicationChartOrgScopeNodeSize(context.externalNodeSize, context.scope);
        }
        return getCommunicationChartNodeSize(context.degree, context.maxDegree, context.nodeSizeMin, context.nodeSizeMax);
    }
    if (isCommunicationChartHubNodeKind(node.kind)) {
        return scaleCommunicationChartHubNodeSize(context.internalMeetNodeSize);
    }
    return context.otherNodeSize;
};
export var buildCommunicationChartNodeElements = function (_a) {
    var data = _a.data, degrees = _a.degrees, maxDegree = _a.maxDegree, width = _a.width, height = _a.height, labelMaxLength = _a.labelMaxLength, nodeSizeMin = _a.nodeSizeMin, nodeSizeMax = _a.nodeSizeMax, externalNodeSize = _a.externalNodeSize, internalMeetNodeSize = _a.internalMeetNodeSize, otherNodeSize = _a.otherNodeSize;
    if (isCommunicationChartEmployeeScope(data.scope) &&
        data.focusUserId != null) {
        var _b = partitionCommunicationChartEmployeeNodes(data.nodes, data.focusUserId), focusNode = _b.focusNode, teammateNodes_1 = _b.teammateNodes, crossTeamNodes_1 = _b.crossTeamNodes, hasExternalAggregateNode_1 = _b.hasExternalAggregateNode;
        var peerNodeSize_1 = resolveCommunicationChartTeamScopePersonNodeSize(nodeSizeMax);
        var focusNodeSize = Math.round(peerNodeSize_1 * COMMUNICATION_CHART_EMPLOYEE_FOCUS_NODE_SIZE_MULTIPLIER);
        var externalNodeSizePx = resolveCommunicationChartTeamScopePersonNodeSize(nodeSizeMax);
        var centerPosition = communicationChartCenterPosition(width, height);
        var nodeElements_1 = [];
        if (focusNode) {
            nodeElements_1.push({
                data: __assign({ id: String(focusNode.nodeId), label: truncateCommunicationChartLabel(focusNode.name, labelMaxLength), fullLabel: focusNode.name, nodeId: focusNode.nodeId, nodeSize: focusNodeSize }, nodePersonData(focusNode)),
                position: centerPosition,
                classes: joinCommunicationChartElementClasses(personNodeClasses(focusNode), 'employeeFocusPerson')
            });
        }
        teammateNodes_1.forEach(function (node, index) {
            nodeElements_1.push({
                data: __assign({ id: String(node.nodeId), label: truncateCommunicationChartLabel(node.name, labelMaxLength), fullLabel: node.name, nodeId: node.nodeId, nodeSize: peerNodeSize_1 }, nodePersonData(node)),
                position: communicationChartEmployeeTeammateColumnPosition(index, teammateNodes_1.length, width, height),
                classes: personNodeClasses(node)
            });
        });
        crossTeamNodes_1.forEach(function (node, index) {
            nodeElements_1.push({
                data: __assign({ id: String(node.nodeId), label: truncateCommunicationChartLabel(node.name, labelMaxLength), fullLabel: node.name, nodeId: node.nodeId, nodeSize: peerNodeSize_1 }, nodePersonData(node)),
                position: communicationChartEmployeeCrossTeamColumnPosition(index, crossTeamNodes_1.length, width, height),
                classes: personNodeClasses(node)
            });
        });
        if (hasExternalAggregateNode_1) {
            nodeElements_1.push({
                data: {
                    id: String(COMMUNICATION_CHART_EXTERNAL_NODE_ID),
                    label: truncateCommunicationChartLabel(COMMUNICATION_CHART_EXTERNAL_NODE_NAME, labelMaxLength),
                    fullLabel: COMMUNICATION_CHART_EXTERNAL_NODE_NAME,
                    nodeId: COMMUNICATION_CHART_EXTERNAL_NODE_ID,
                    nodeSize: externalNodeSizePx
                },
                position: communicationChartExternalNodePosition(width, height),
                classes: CommunicationChartNodeKindEnum.External
            });
        }
        return nodeElements_1;
    }
    var _c = partitionCommunicationChartNodes(data.nodes), teamMemberNodes = _c.teamMemberNodes, crossTeamNodes = _c.crossTeamNodes, syntheticNodes = _c.syntheticNodes, hasExternalAggregateNode = _c.hasExternalAggregateNode, hasCrossTeamAggregateNode = _c.hasCrossTeamAggregateNode;
    var isDashboardTeamScope = isCommunicationChartTeamScope(data.scope);
    var teamScopePeerNodeSize = resolveCommunicationChartTeamScopePersonNodeSize(nodeSizeMax);
    var teamScopeManagerNodeSize = resolveCommunicationChartTeamScopeManagerNodeSize(nodeSizeMax);
    var teamScopeSyntheticNodeSize = teamScopePeerNodeSize;
    var useOrgTeamHubReveal = usesCommunicationChartOrgTeamHubReveal(data.scope);
    var layoutContext = {
        teamMemberNodes: teamMemberNodes,
        isDashboardTeamScope: isDashboardTeamScope,
        hasExternalAggregateNode: hasExternalAggregateNode,
        hasCrossTeamAggregateNode: hasCrossTeamAggregateNode,
        width: width,
        height: height
    };
    var nodeElements = teamMemberNodes.map(function (node, index) {
        var _a;
        var fullLabel = node.name;
        var degree = (_a = degrees.get(node.nodeId)) !== null && _a !== void 0 ? _a : 0;
        var isOrgTeam = isCommunicationChartOrgTeamNode(node, data.scope);
        var memberCount = isOrgTeam ? node.memberCount : undefined;
        var baseNodeSize = scaleCommunicationChartOrgScopeNodeSize(resolveCommunicationChartNodeSize(node, degree, maxDegree, nodeSizeMin, nodeSizeMax), data.scope);
        var isTeamScopeManager = isDashboardTeamScope &&
            data.managerUserId != null &&
            node.nodeId === data.managerUserId;
        var nodeSize = isOrgTeam && memberCount !== undefined
            ? resolveCommunicationChartOrgTeamBucketedNodeSize(baseNodeSize, memberCount)
            : isTeamScopeManager
                ? teamScopeManagerNodeSize
                : isDashboardTeamScope
                    ? teamScopePeerNodeSize
                    : baseNodeSize;
        return {
            data: __assign(__assign({ id: String(node.nodeId), label: truncateCommunicationChartLabel(fullLabel, labelMaxLength), fullLabel: fullLabel, nodeId: node.nodeId, nodeSize: nodeSize }, nodePersonData(node)), (memberCount !== undefined ? { memberCount: memberCount } : {})),
            position: communicationChartRingNodePosition(index, teamMemberNodes.length, width, height),
            classes: joinCommunicationChartElementClasses(personNodeClasses(node, {
                teamScopeDashboardPerson: isDashboardTeamScope
            }), isTeamScopeManager && 'teamScopeManagerPerson', isOrgTeam && 'orgTeam')
        };
    });
    crossTeamNodes.forEach(function (node, index) {
        var _a;
        var fullLabel = node.name;
        var degree = (_a = degrees.get(node.nodeId)) !== null && _a !== void 0 ? _a : 0;
        nodeElements.push({
            data: __assign({ id: String(node.nodeId), label: truncateCommunicationChartLabel(fullLabel, labelMaxLength), fullLabel: fullLabel, nodeId: node.nodeId, nodeSize: resolveCommunicationChartNodeSize(node, degree, maxDegree, nodeSizeMin, nodeSizeMax) }, nodePersonData(node)),
            position: communicationChartCrossTeamColumnPosition(index, crossTeamNodes.length, width, height),
            classes: personNodeClasses(node)
        });
    });
    syntheticNodes.forEach(function (node) {
        var _a;
        var fullLabel = node.name;
        var degree = (_a = degrees.get(node.nodeId)) !== null && _a !== void 0 ? _a : 0;
        var hubTeamId = resolveHubSatelliteTeamId(node);
        var isHubSatellite = isCommunicationChartHubNodeKind(node.kind);
        var position = resolveSyntheticNodePosition(node, layoutContext);
        nodeElements.push(__assign(__assign({ data: __assign(__assign({ id: String(node.nodeId), label: truncateCommunicationChartLabel(fullLabel, labelMaxLength), fullLabel: fullLabel, nodeId: node.nodeId }, (useOrgTeamHubReveal && hubTeamId !== null ? { hubTeamId: hubTeamId } : {})), { nodeSize: resolveSyntheticNodeSize(node, {
                    scope: data.scope,
                    isDashboardTeamScope: isDashboardTeamScope,
                    teamScopeSyntheticNodeSize: teamScopeSyntheticNodeSize,
                    externalNodeSize: externalNodeSize,
                    internalMeetNodeSize: internalMeetNodeSize,
                    otherNodeSize: otherNodeSize,
                    degree: degree,
                    maxDegree: maxDegree,
                    nodeSizeMin: nodeSizeMin,
                    nodeSizeMax: nodeSizeMax
                }) }) }, (position ? { position: position } : {})), { classes: joinCommunicationChartElementClasses(resolveSyntheticNodeKindClass(node), useOrgTeamHubReveal && isHubSatellite && 'teamHubSatellite', useOrgTeamHubReveal && isHubSatellite && 'hubHidden') }));
    });
    return nodeElements;
};
